<template>
    <div>
        <div class="section-divider">
            <span class="section-title">Balance Changes</span>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-2">Loading transfers...</p>
        </div>

        <div v-else-if="error" class="compass-info-box compass-info-box--danger">
            <p class="mb-0">{{ error }}</p>
        </div>

        <div v-else-if="changes.length === 0" class="compass-info-box">
            <p class="mb-0">No balance changes found for this wallet.</p>
        </div>

        <div v-else>
            <div class="table-mobile-scroll">
                <table class="compass-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Token</th>
                            <th class="td-num">Change</th>
                            <th class="td-num">Value</th>
                            <th>Counterparty</th>
                            <th>Type</th>
                            <th>Tx</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in changes" :key="c.signature + (c.instruction_index || '')">
                            <td>
                                <span class="text-muted small">{{ formatTime(c.timestamp) }}</span>
                            </td>
                            <td>
                                <a v-if="c.mint" :href="`/tokens/${c.mint}`" class="text-decoration-none fw-semibold">
                                    {{ c.symbol || formatPubkey(c.mint) }}
                                </a>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td class="td-num mono-num">
                                <span :class="c.change >= 0 ? 'text-success' : 'text-danger'">
                                    {{ c.change >= 0 ? '+' : '' }}{{ formatAmount(c.change) }}
                                </span>
                            </td>
                            <td class="td-num mono-num">
                                <span v-if="c.change_usd" :class="c.change_usd >= 0 ? 'text-success' : 'text-danger'">
                                    {{ c.change_usd >= 0 ? '+' : '' }}${{ formatAbsValue(c.change_usd) }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                                <div v-if="c.counterparty">
                                    <a
                                        :href="`/address/${c.counterparty}`"
                                        class="font-mono text-muted small text-decoration-none"
                                    >{{ c.counterparty_name || formatPubkey(c.counterparty) }}</a>
                                </div>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                                <span v-if="c.source_type" class="compass-badge compass-badge--outline" style="font-size: 0.7rem;">
                                    {{ c.source_type }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                                <a
                                    :href="`https://solscan.io/tx/${c.signature}`"
                                    target="_blank"
                                    class="font-mono text-muted small"
                                >{{ formatPubkey(c.signature, 4, 4) }}</a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.has_more || pagination.offset > 0">
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="pagination.offset === 0"
                    @click="loadPage(pagination.offset - pagination.limit)"
                >Previous</button>
                <span class="text-muted small">
                    Showing {{ pagination.offset + 1 }}-{{ Math.min(pagination.offset + changes.length, pagination.total) }}
                    of {{ pagination.total }}
                </span>
                <button
                    class="compass-btn compass-btn--sm"
                    :disabled="!pagination.has_more"
                    @click="loadPage(pagination.offset + pagination.limit)"
                >Next</button>
            </div>
        </div>
    </div>
</template>

<script>
import { formatPubkey } from '../composables/useFormatters';

export default {
    name: 'WalletTransfers',
    props: {
        address: { type: String, required: true },
    },
    data() {
        return {
            changes: [],
            pagination: { offset: 0, limit: 50, total: 0, has_more: false },
            loading: true,
            error: null,
        };
    },
    methods: {
        formatPubkey,
        formatTime(time) {
            if (!time) return '';
            const d = new Date(time);
            const now = new Date();
            const diffDays = Math.floor((now - d) / 86400000);
            if (diffDays === 0) {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (diffDays < 7) {
                return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
            }
            return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        },
        formatAmount(val) {
            if (!val) return '0';
            const abs = Math.abs(val);
            if (abs >= 1e9) return (val / 1e9).toFixed(2) + 'B';
            if (abs >= 1e6) return (val / 1e6).toFixed(2) + 'M';
            if (abs >= 1e3) return (val / 1e3).toFixed(1) + 'K';
            if (abs < 0.001 && abs > 0) return val.toExponential(2);
            return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
        formatAbsValue(val) {
            const abs = Math.abs(val);
            if (abs >= 1e6) return (abs / 1e6).toFixed(2) + 'M';
            if (abs >= 1e3) return (abs / 1e3).toFixed(1) + 'K';
            return abs.toFixed(2);
        },
        async fetchChanges(offset = 0) {
            this.loading = true;
            this.error = null;
            try {
                const resp = await fetch(`/api/v1/wallets/${this.address}/balance-history?limit=50&offset=${offset}`);
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = await resp.json();
                const data = json.data || json;
                this.changes = data.changes || [];
                this.pagination = data.pagination || { offset: 0, limit: 50, total: 0, has_more: false };
            } catch (e) {
                this.error = 'Failed to load transfers. ' + e.message;
            } finally {
                this.loading = false;
            }
        },
        loadPage(offset) {
            this.fetchChanges(Math.max(0, offset));
        },
    },
    mounted() {
        this.fetchChanges();
    },
};
</script>
